"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { MapPin, Phone, Linkedin, Globe, ShieldCheck } from "lucide-react";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-wrap justify-center gap-6">
        {[1, 2, 3].map(i => <div key={i} className="h-4 w-20 bg-muted animate-pulse rounded-sm" />)}
      </div>;
  }
  return <ul className="flex flex-wrap justify-center gap-x-8 gap-y-2">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-[11px] font-mono uppercase tracking-widest text-foreground/60 hover:text-primary transition-colors duration-200">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
export default function Footer() {
  const services = [{
    name: "Medical Remote Interpretation",
    path: "/ServicesPage"
  }, {
    name: "Legal Remote Interpretation",
    path: "/ServicesPage"
  }, {
    name: "Specialized Translation",
    path: "/ServicesPage"
  }];
  const contactDepartments = [{
    title: "Partnerships",
    email: "management@glyphicls.com"
  }, {
    title: "Billing & Accounts",
    email: "billing@glyphicls.com"
  }, {
    title: "Recruitment",
    email: "talent@glyphicls.com"
  }];
  return <section id="footer" className="bg-background text-foreground border-t border-border/10 pt-16 pb-10">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-4 mb-16">
          
          {}
          <div className="lg:col-span-4 space-y-8">
            <Link to="/" className="inline-block">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1649121/img/glyphic_logo_main.png" alt="GLYPHIC S.A.S." className="h-7 w-auto" />
            </Link>
            <div className="space-y-4 max-w-sm">
              <p className="font-serif text-lg text-foreground/80 leading-relaxed italic">
                "Bridging the language gap with professional precision and ethical integrity."
              </p>
              <p className="text-sm text-foreground/60 leading-relaxed">
                Specialized Language Service Company providing high-performance Medical and Legal Remote Interpretation with 100% Ethernet-wired infrastructure.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Link to="https://www.linkedin.com/company/glyphic-global/" className="p-2 rounded-sm border border-border/50 text-foreground/40 hover:text-primary hover:border-primary/30 transition-all duration-200" aria-label="LinkedIn" newTab>
                <Linkedin className="size-4" />
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-2">
            <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] mb-8 text-primary font-bold">
              Core Services
            </h3>
            <ul className="space-y-4">
              {services.map((service, i) => <li key={i} data-index={i}>
                  <Link to={service.path} className="text-sm text-foreground/70 hover:text-primary transition-colors duration-200 block group flex items-center gap-2">
                    <span className="size-1 bg-primary/20 group-hover:bg-primary transition-colors rounded-full" />
                    {service.name}
                  </Link>
                </li>)}
            </ul>
          </div>

          {}
          <div className="lg:col-span-3">
            <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] mb-8 text-primary font-bold">
              Connect
            </h3>
            <div className="space-y-4">
              {contactDepartments.map((dept, i) => <div key={i} data-index={i} className="flex flex-col">
                  <span className="font-mono text-[10px] text-foreground/80">
                    {dept.email}
                  </span>
                </div>)}
            </div>
          </div>

          {}
          <div className="lg:col-span-3">
            <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] mb-8 text-primary font-bold">
              Global Operations
            </h3>
            <ul className="space-y-6">
              <li className="flex items-start gap-4">
                <MapPin className="size-3 shrink-0 text-foreground/50 mt-1" />
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-foreground/40">Headquarters</span>
                  <span className="text-sm font-medium text-foreground/80">Pereira, Colombia</span>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <Phone className="size-3 shrink-0 text-foreground/50 mt-1" />
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-foreground/40">Direct Line</span>
                  <span className="text-sm font-mono text-foreground/80">+57 301 753 2271</span>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <ShieldCheck className="size-3 shrink-0 text-foreground/50 mt-1" />
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-foreground/40">Compliance</span>
                  <span className="text-sm font-medium text-foreground/80">HIPAA & Legal Standards</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <Separator className="bg-border/10 mb-10" />

        <div className="flex flex-col items-center gap-8">
          <div className="w-full">
            <MenuProvider menu_id="46">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>
          
          <div className="flex flex-col items-center gap-4">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Globe className="size-3 text-foreground/30" />
                <span className="font-mono text-[9px] uppercase tracking-widest text-foreground/40">Global Reach</span>
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="size-3 text-foreground/30" />
                <span className="font-mono text-[9px] uppercase tracking-widest text-foreground/40">Secure Infrastructure</span>
              </div>
            </div>
            <div className="text-center space-y-2">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/50 [font-variant:small-caps]">
                © 2026 GLYPHIC S.A.S. | Professional Precision & Academic Rigor.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}